import React, { useEffect, useRef, useState } from 'react';
import { getEmployees } from '@/modules/orders/redux/OrderCRUD';
import { getMaterialRequests, getMaterialRequest } from '@/modules/inventory/requestMaterial/redux/RequestMaterialCRUD';
import { getItems } from '@/modules/inventory/items/redux/ItemCRUD';
import TextareaAutosize from 'react-textarea-autosize';
import { Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';

import { LabeledField } from '@/__core/components/LabeledField';
import { ItemSupplierAddModal } from '@/modules/inventory/items/components/modals';
import FileAttachmentField from '@/__core/components/FileAttachmentField';
import axiosInstance from '@/setup/axios/axiosInstance';
import { usePermissions } from '@/__core/permissions/hooks/usePermissions';
import { usePermissionChecker } from '@/__core/permissions/hooks/usePermissionChecker';

import { StatusManageItem } from '@/modules/settings/redux/SettingsCRUD';
import { PurchasingSupplierOption } from '../hooks/usePurchasingSuppliers';
import { InventoryPurchaseOrderFormData, PurchaseOrderLine } from '../types';
import PurchaseOrderLinesTable from './PurchaseOrderLinesTable';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import { UserOptionDisplay } from '@/__core/components';
import useRequestMaterialWarehouses from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialWarehouses';
import { RequestMaterialItemOption } from '@/modules/inventory/requestMaterial/types';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

type AddOptionCustomDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>;
  loading?: boolean;
};

interface WarehouseSelectProps {
  companyId?: string;
  value?: string;
  warehouseName?: string;
  onChange: (warehouseId: string | null, warehouseName?: string) => void;
  onInitialize?: (warehouseId: string, warehouseName: string) => void;
  disabled?: boolean;
}

function WarehouseSelect({ companyId, value, warehouseName, onChange, onInitialize, disabled = false }: WarehouseSelectProps) {
  const { warehouseOptions, loading } = useRequestMaterialWarehouses({ companyId: companyId || '', skip: false, onError: () => {} });

  const effectiveOptions = (warehouseOptions && warehouseOptions.length > 0)
    ? warehouseOptions
    : (value ? [{ id: value, name: warehouseName || value }] : []);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!value && effectiveOptions.length > 0 && !loading && !initializedRef.current) {
      const defaultStore = effectiveOptions.find((opt) => opt.is_default);
      if (defaultStore) {
        initializedRef.current = true;
        (onInitialize || onChange)(defaultStore.id, defaultStore.name);
      }
    }
  }, [effectiveOptions, loading, value, onChange, onInitialize]);

  return (
    <TextField
      select
      size='small'
      value={value || ''}
      onChange={(e) => {
        const next = e.target.value || null;
        const found = effectiveOptions.find((w) => w.id === next) as any;
        onChange(next, found ? found.name : undefined);
      }}
      disabled={disabled}
      fullWidth
      SelectProps={{ displayEmpty: true }}
    >
      {loading && <MenuItem value=''><em>Đang tải...</em></MenuItem>}
      {!loading && effectiveOptions.length === 0 && <MenuItem value=''><em>-- Chưa có kho --</em></MenuItem>}
      {!loading && effectiveOptions.map((w) => (
        <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
      ))}
    </TextField>
  );
}



function EmployeeSelect({ value, name, onChange, disabled = false, companyId }: any) {
  const [options, setOptions] = useState<Array<{ id: string; name: string; nickname?: string; avatar?: string; image1?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = React.useRef<number | null>(null);

  const load = async (search?: string) => {
    setLoading(true);
    try {
      const resp = await getEmployees(search || undefined, companyId || undefined);
      const payload = Array.isArray(resp) ? resp : (resp?.data?.results || resp?.data || []);
      const mapped = payload.map((emp: any) => ({
        id: emp.id || emp.value,
        name: emp.name || emp.nickname || emp.user?.full_name || emp.full_name || emp.label || '',
        nickname: emp.nickname || '',
        avatar: emp.user?.avatar || null,
        image1: emp.image1 || null,
      }));
      setOptions(mapped);
    } catch (_err) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <AddButtonAutocomplete
      options={options}
      value={options.find((o) => o.id === value) || (value ? { id: value, name: name || value } : null)}
      onChange={(_e, newVal) => {
        const v = newVal as any;
        onChange(v ? v.id : null, v ? v.name : null);
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === 'input' || reason === 'clear') {
          if (searchRef.current) window.clearTimeout(searchRef.current);
          searchRef.current = window.setTimeout(() => void load(v), 250) as unknown as number;
        }
      }}
      placeholder='Chọn hoặc tìm nhân viên'
      noOptionsText='Không tìm thấy nhân viên'
      loading={loading}
      disabled={disabled}
      getOptionLabel={(option) => option?.name || ''}
      renderOption={(props, option) => (
        <Box component='li' {...props}>
          <UserOptionDisplay user={option} />
        </Box>
      )}
      fullWidth
    />
  );
}

function MaterialRequestSelect({ value, name, onChange, disabled = false, companyId }: any) {
  const [options, setOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = React.useRef<number | null>(null);

  const load = async (search?: string) => {
    setLoading(true);
    try {
      const resp = await getMaterialRequests({ companyId: companyId || undefined, search: search || undefined, pageSize: 50 });
      const payload = resp?.results || [];
      const mapped = payload.map((r: any) => ({ id: r.id, name: r.request_no || r.id }));
      setOptions(mapped);
    } catch (_err) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <AddButtonAutocomplete
      options={options}
      value={options.find((o) => o.id === value) || (value ? { id: value, name: name || '' } : null)}
      onChange={(_e, newVal) => {
        const v = newVal as any;
        onChange(v ? v.id : null, v ? v.name : null);
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === 'input' || reason === 'clear') {
          if (searchRef.current) window.clearTimeout(searchRef.current);
          searchRef.current = window.setTimeout(() => void load(v), 250) as unknown as number;
        }
      }}
      placeholder='Chọn phiếu đề xuất VT'
      noOptionsText='Không tìm thấy phiếu đề xuất'
      loading={loading}
      disabled={disabled}
      fullWidth
    />
  );
}

const SupplierAddModal: React.FC<AddOptionCustomDialogProps> = (props) => <ItemSupplierAddModal {...props} />;

const purchaseTypeOptions = [
  { id: 'reserve', name: 'Mua dự trữ' },
  { id: 'order', name: 'Mua theo đơn hàng' },
];

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : value;
};

interface PurchaseOrderFormProps {
  formData: InventoryPurchaseOrderFormData;
  statusOptions: StatusManageItem[];
  supplierOptions: PurchasingSupplierOption[];
  supplierLoading?: boolean;
  onSupplierSearch: (search: string) => void;
  onCreateSupplier: (data: { name: string; email?: string; phone?: string; address?: any }) => Promise<PurchasingSupplierOption>;
  onChangeForm: (updates: Partial<InventoryPurchaseOrderFormData>) => void;
  onInitializeForm?: (updates: Partial<InventoryPurchaseOrderFormData>) => void;
  onChangeLine: (lineId: string, updates: Partial<PurchaseOrderLine>) => void;
  onSave: () => void;
  onFilesChange?: (files: File[]) => void;
  purchaseOrderId?: string;
  reloadDocKey?: number;
  canEdit?: boolean;
  isDirty?: boolean;
  saving?: boolean;
  embedded?: boolean;
}

export default function PurchaseOrderForm({
  formData,
  statusOptions,
  supplierOptions,
  supplierLoading = false,
  onSupplierSearch,
  onCreateSupplier,
  onChangeForm,
  onInitializeForm,
  onChangeLine,
  onSave,
  onFilesChange,
  purchaseOrderId,
  reloadDocKey,
  canEdit = true,
  isDirty = false,
  saving = false,
  embedded = false,
}: PurchaseOrderFormProps) {
  usePermissions();
  const { canCreate } = usePermissionChecker();
  const canCreateSupplier = canCreate('supplier') || canCreate('suppliers');
  const canCreateItem = canCreate('item') || canCreate('inventory.item');
  const readOnly = !canEdit;
  const selectedSupplier = supplierOptions.find((s) => s.id === formData.supplier_id) || null;
  const [itemOptions, setItemOptions] = useState<RequestMaterialItemOption[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const hasFileChanges = selectedFiles.length > 0;
  const effectiveIsDirty = isDirty || hasFileChanges;
  const isReservePurchase = formData.purchase_type === 'reserve';

  useEffect(() => {
    if (!purchaseOrderId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingDocuments(true);
        const { getContentTypeId, getDocumentsByObject } = await import('@/modules/documents/redux/DocumentCRUD');
        let contentTypeId: number;
        try {
          contentTypeId = await getContentTypeId('purchase');
        } catch {
          contentTypeId = await getContentTypeId('purchaseorder');
        }
        const docs = await getDocumentsByObject(contentTypeId, purchaseOrderId);
        if (!cancelled) setUploadedDocuments(docs);
      } catch {
        if (!cancelled) setUploadedDocuments([]);
      } finally {
        if (!cancelled) setLoadingDocuments(false);
      }
    })();
    return () => { cancelled = true; };
  }, [purchaseOrderId, reloadDocKey]);

  useEffect(() => {
    if (reloadDocKey !== undefined) {
      setSelectedFiles([]);
    }
  }, [reloadDocKey]);

  useEffect(() => {
    let mounted = true;
    const companyId = formData.company_id;

    if (!companyId) {
      setItemOptions([]);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        const response = await getItems({ companyId, pageSize: 50 });
        if (!mounted) {
          return;
        }

        const mapped: RequestMaterialItemOption[] = (response.results || []).map((item) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          unit_id: item.uom_id || undefined,
          unit_name: item.uom_name || undefined,
          supplier_id: item.supplier_id || undefined,
          supplier_name: item.supplier_name || undefined,
          last_price: item.last_price || undefined,
        }));
        setItemOptions(mapped);
      } catch (_error) {
        if (mounted) {
          setItemOptions([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [formData.company_id]);

  const effectiveStatusOptions = React.useMemo(() => {
    const normalizedOptions = (statusOptions || []).filter((option) => Boolean(option?.id));

    if (!formData.status) {
      return normalizedOptions;
    }

    const hasCurrentStatus = normalizedOptions.some((option) => option.id === formData.status);
    if (hasCurrentStatus) {
      return normalizedOptions;
    }

    return [
      ...normalizedOptions,
      {
        id: formData.status,
        name: formData.status_display || formData.status_code || formData.status,
        color: formData.status_color || '',
      } as StatusManageItem,
    ];
  }, [formData.status, formData.status_code, formData.status_display, formData.status_color, statusOptions]);

  // Ensure new POs get the default status once statusOptions are loaded
  useEffect(() => {
    if (!formData.status || formData.status === '') {
      const defaultStatus = statusOptions.find((o) => o.is_default);
      if (defaultStatus?.id) {
        onChangeForm({
          status: defaultStatus.id,
          status_display: defaultStatus.name,
          status_color: defaultStatus.color || '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions, formData.status]);

  const hydratedMaterialRequestIdRef = React.useRef<string | null>(null);

  // When a material request is selected, load its items into the PO lines (once per MR id)
  useEffect(() => {
    let mounted = true;
    const mrId = formData.material_request_id;

    if (!mrId) {
      hydratedMaterialRequestIdRef.current = null;
      return;
    }

    if (hydratedMaterialRequestIdRef.current === mrId) {
      return;
    }

    hydratedMaterialRequestIdRef.current = mrId;

    (async () => {
      try {
        const resp = await getMaterialRequest(mrId);
        if (!mounted || !resp) return;

        const existingLines: PurchaseOrderLine[] = formData.lines || [];

        const mappedLines = (resp.lines || []).map((ln: any, idx: number) => {
          const base = {
            id: String(ln.id ?? `mr-line-${idx}`),
            sort_order: ln.sort_order ?? idx + 1,
            item_id: ln.item_id || (ln.item && ln.item.id) || '',
            item_code: ln.item_code || (ln.item && (ln.item.code || ln.item.item_code)) || '',
            item_name: ln.item_name || (ln.item && (ln.item.name || ln.item.item_name)) || '',
            uom_id: ln.uom_id || '',
            uom_name: ln.uom_name || (ln.uom && ln.uom.name) || '',
            requested_qty: ln.requested_qty != null ? String(ln.requested_qty) : '0',
            ordered_qty: ln.requested_qty != null ? String(ln.requested_qty) : '0',
            received_qty: '0',
            remaining_qty: ln.requested_qty != null ? String(ln.requested_qty) : '0',
            unit_price: ln.unit_price ?? ln.unitPrice ?? ln.price ?? '',
            line_amount: ln.line_amount ?? ln.lineAmount ?? ln.amount ?? '',
            manufacturer_id: null,
            manufacturer_name: null,
            source_request_line_id: ln.id != null ? String(ln.id) : null,
            note: '',
          } as PurchaseOrderLine;

          const matched = existingLines.find((ex) => (
            (ex.source_request_line_id && ln.id && ex.source_request_line_id === String(ln.id)) ||
            (ex.item_id && ln.item_id && ex.item_id === ln.item_id)
          ));

          if (matched) {
            if (matched.unit_price) base.unit_price = matched.unit_price;
            if (matched.line_amount) base.line_amount = matched.line_amount;
            if (matched.note) base.note = matched.note;
            if (matched.ordered_qty) base.ordered_qty = matched.ordered_qty;
          }

          return base;
        });

        onChangeForm({ lines: mappedLines });
      } catch (_err) {
        if (hydratedMaterialRequestIdRef.current === mrId) {
          hydratedMaterialRequestIdRef.current = null;
        }
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.material_request_id]);
  const handleAddItem = () => {
    const existingLines = formData.lines || [];
    const maxSortOrder = existingLines.reduce((max, line) => Math.max(max, Number(line.sort_order || 0)), 0);
    const nextLine: PurchaseOrderLine = {
      id: `tmp-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      sort_order: maxSortOrder + 1,
      item_id: '',
      item_code: '',
      item_name: '',
      uom_id: '',
      uom_name: '',
      requested_qty: '0',
      ordered_qty: '0',
      received_qty: '0',
      remaining_qty: '0',
      unit_price: '0',
      line_amount: '0',
      manufacturer_id: null,
      manufacturer_name: null,
      source_request_line_id: null,
      note: '',
    };

    onChangeForm({ lines: [...existingLines, nextLine] });
  };

  const handleCreateItem = (createdItem: RequestMaterialItemOption) => {
    setItemOptions((current) => {
      if (current.some((item) => item.id === createdItem.id)) {
        return current;
      }
      return [createdItem, ...current];
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => {
        const next = [...prev, ...files];
        onFilesChange?.(next);
        return next;
      });
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      onFilesChange?.(next);
      return next;
    });
  };

  const handleRemoveUploadedDocument = async (documentId: string) => {
    try {
      const { deleteDocument } = await import('@/modules/documents/redux/DocumentCRUD');
      await deleteDocument(documentId);
      setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    } catch {
      // silently fail
    }
  };

  const handleFileDownload = async (documentId: string) => {
    try {
      const response = await axiosInstance.get(`documents/${documentId}/download/`, { responseType: 'blob' });
      const blob = response.data;
      const fileName = uploadedDocuments.find((doc) => doc.id === documentId)?.name || 'download';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  return (
    <Box
      sx={{
        pt: 0,
        pb: 2,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        ...(embedded ? {
          overflowX: 'hidden',
          '& .MuiGrid-container': {
            width: '100%',
            maxWidth: '100%',
            marginLeft: 0,
            marginRight: 0,
          },
          '& .MuiGrid-root': {
            minWidth: 0,
            boxSizing: 'border-box',
          },
          '& .MuiPaper-root': {
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          },
        } : {}),
      }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Grid container rowSpacing={0} columnSpacing={4}>

          {/* Mã PO – read-only */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã PO' labelWidth={100}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', fontFamily: '"Roboto Mono", monospace' }}>
                  {formData.po_no || '—'}
                </Typography>
              </Box>
            </LabeledField>
          </Grid>

          {/* Loại mua */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Loại mua' labelWidth={100}>
              <TextField
                select
                size='small'
                value={formData.purchase_type}
                disabled={readOnly}
                onChange={(e) => {
                  const nextPurchaseType = e.target.value;
                  if (nextPurchaseType === 'reserve') {
                    onChangeForm({
                      purchase_type: nextPurchaseType,
                      material_request_id: null,
                      material_request_no: null,
                    });
                    return;
                  }

                  onChangeForm({ purchase_type: nextPurchaseType });
                }}
                fullWidth
              >
                {purchaseTypeOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
            </LabeledField>
          </Grid>

          {/* Kho nhận */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho nhận' labelWidth={100}>
              {/* Use the same warehouse hook as RequestMaterialForm to load warehouses for the selected company */}
              <WarehouseSelect
                companyId={formData.company_id}
                value={formData.warehouse_id}
                warehouseName={formData.warehouse_name}
                disabled={readOnly}
                onInitialize={(warehouseId, warehouseName) => onInitializeForm?.({ warehouse_id: warehouseId, warehouse_name: warehouseName })}
                onChange={(warehouseId: string | null, warehouseName?: string) => onChangeForm({ warehouse_id: warehouseId || '', warehouse_name: warehouseName || '' })}
              />
            </LabeledField>
          </Grid>

          {/* Nhà cung cấp */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Nhà cung cấp' labelWidth={100}>
              <AddButtonAutocomplete
                options={supplierOptions}
                value={selectedSupplier}
                onChange={(_event, newValue) => {
                  const s = newValue as PurchasingSupplierOption | null;
                  onChangeForm({ supplier_id: s?.id || '', supplier_name: s?.name || '' });
                }}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input' || reason === 'clear') onSupplierSearch(value);
                }}
                loading={supplierLoading}
                filterOptions={(options) => options}
                placeholder='Chọn nhà cung cấp'
                noOptionsText='Không tìm thấy nhà cung cấp'
                disabled={readOnly}
                canAddNew={!readOnly && canCreateSupplier}
                CustomDialog={SupplierAddModal}
                onAddNewCustom={async (data) => {
                  if (readOnly) {
                    return selectedSupplier || { id: '', name: '' };
                  }
                  const created = await onCreateSupplier(data);
                  onChangeForm({ supplier_id: created.id, supplier_name: created.name });
                  return created;
                }}
                fullWidth
              />
            </LabeledField>
          </Grid>

          {/* Trạng thái */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <TextField
                select
                size='small'
                value={formData.status || ''}
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  const selectedStatus = effectiveStatusOptions.find((option) => option.id === nextStatus);
                  onChangeForm({
                    status: nextStatus,
                    status_display: selectedStatus?.name || '',
                    status_color: selectedStatus?.color || '',
                  });
                }}
                fullWidth
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) return <em>-- Chưa có trạng thái --</em>;
                    const option = effectiveStatusOptions.find((o) => o.id === selected);
                    return (
                      <Typography sx={{ color: option?.color || 'inherit', fontWeight: 600, fontSize: '0.875rem' }}>
                        {option?.name || String(selected)}
                      </Typography>
                    );
                  },
                }}
              >
                {effectiveStatusOptions.length === 0 && <MenuItem value=''><em>-- Chưa có trạng thái --</em></MenuItem>}
                {effectiveStatusOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id ?? ''}>
                    <Typography sx={{ color: o.color || 'inherit', fontWeight: 600 }}>
                      {o.name}
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
            </LabeledField>
          </Grid>

          {/* Người đề xuất */}
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Người đề xuất' labelWidth={100}>
              <EmployeeSelect
                value={formData.requested_by_id || ''}
                name={formData.requested_by_name || ''}
                companyId={formData.company_id}
                onChange={(id: string | null, name?: string | null) => onChangeForm({ requested_by_id: id || null, requested_by_name: name || null })}
                disabled={readOnly}
              />
            </LabeledField>
          </Grid>

          {/* Phiếu đề xuất VT */}
          {!isReservePurchase && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Phiếu ĐXVT' labelWidth={80}>
                <MaterialRequestSelect
                  value={formData.material_request_id || ''}
                  name={formData.material_request_no || ''}
                  companyId={formData.company_id}
                  onChange={(id: string | null, name?: string | null) => onChangeForm({ material_request_id: id || null, material_request_no: name || null })}
                  disabled={readOnly}
                />
              </LabeledField>
            </Grid>
          )}

          {/* Đơn hàng – read-only reference */}
          {formData.order_id && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Đơn hàng' labelWidth={80}>
                <Typography sx={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: 500, py: 0.625, fontFamily: '"Roboto Mono", monospace' }}>
                  {formData.order_no || formData.order_id}
                </Typography>
              </LabeledField>
            </Grid>
          )}

          {/* Người duyệt – read-only (system-set) */}
          {formData.approved_by_name && (
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Người duyệt' labelWidth={100}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#1e293b' }}>
                    {formData.approved_by_name}
                  </Typography>
                  {formData.approved_at && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                      ({formatDate(formData.approved_at)})
                    </Typography>
                  )}
                </Box>
              </LabeledField>
            </Grid>
          )}

      </Grid>
      </Paper>

      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          px: embedded ? 2 : 0,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
      <Box sx={{ pt: 0.5, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
        <PurchaseOrderLinesTable
          lines={formData.lines ?? []}
          onChangeLine={onChangeLine}
          onAddItem={handleAddItem}
          itemOptions={itemOptions}
          onCreateItem={handleCreateItem}
          onRemoveLine={(lineId) => onChangeForm({ lines: (formData.lines ?? []).filter((l) => l.id !== lineId) })}
          companyId={formData.company_id}
          companyName={formData.company_name}
          canEdit={canEdit}
          canCreateItem={canCreateItem}
        />
      </Box>
        <Box sx={{ mt: 2, width: '100%' }}>
      <LabeledField label='Ghi chú' alignItems='start' labelWidth={140}>
          <TextareaAutosize
            minRows={2}
            value={formData.note || ''}
            disabled={readOnly}
            onChange={(e) => onChangeForm({ note: e.target.value })}
            placeholder='Nhập ghi chú cho phiếu mua...'
            spellCheck={false}
            autoComplete='off'
            readOnly={readOnly}
            style={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              padding: '16px 18px',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
              backgroundColor: '#fff',
            }}
          />
        </LabeledField>
            </Box>
      <Grid container rowSpacing={0} columnSpacing={4} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <Grid size={{ xs: 12 }}>
          <LabeledField label='File đính kèm' labelWidth={140}>
            <FileAttachmentField
              documents={uploadedDocuments}
              selectedFiles={selectedFiles}
              disabled={readOnly}
              loading={loadingDocuments}
              fileInputKey='po-file-input'
              onFileSelect={handleFileSelect}
              onRemoveFile={handleRemoveFile}
              onRemoveDocument={handleRemoveUploadedDocument}
              onPreview={async (docId) => {
                try {
                  const response = await axiosInstance.get(`documents/${docId}/download/`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(response.data);
                  window.open(url, '_blank');
                } catch {
                  // silently fail
                }
              }}
              onDownload={handleFileDownload}
            />
          </LabeledField>
        </Grid>
      </Grid>

      <Box sx={{ pt: 1, width: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='flex-end' alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Button variant='contained' onClick={onSave} disabled={saving || !effectiveIsDirty || readOnly} size='small'>
            {saving ? 'Đang lưu...' : 'Lưu phiếu mua'}
          </Button>
        </Stack>
      </Box>
      </Box>
    </Box>
  );
}
